// Session of the signed-in user. The client authenticates with the session cookie (Laravel Sanctum
// "SPA authentication"): the login is a Microsoft OAuth round trip (see login() and OAuthCallbackPage),
// after that the cookie is sent with every API request. There is no token in the page or in a header.
import { reactive } from 'vue';
import axios from 'axios';

// Base URL of the server (API, OAuth login). Empty = the page and the API share an origin.
export const API_URL = import.meta.env.VITE_API_URL || '';

export const auth = reactive({
    // the signed-in user as returned by GET /api/user/me (attributes, roles, is_admin, ...)
    user: null,
    // set when the server could not be reached at all (as opposed to "not signed in")
    unreachable: false,

    async load(config = {}) {
        const response = await axios.get('/api/user/me', config);
        this.user = response.data.data;
    },

    // ends the session on the server; the caller goes to the login page
    async logout() {
        try {
            await axios.post('/api/logout');
        } finally {
            this.user = null;
        }
    },

    hasRole(role) {
        return !!this.user && this.user.roles.includes(role);
    },
});

// Starts the Microsoft login: the server builds the authorize URL (and remembers the state in the session),
// the browser goes there and Microsoft sends it back to the client's /oauth/callback.
export async function startLogin() {
    const response = await axios.get('/api/auth/microsoft/url');
    window.location.href = response.data.url;
}

// Finishes it: the server verifies the code and state with Microsoft and signs the user in (session cookie).
export async function finishLogin(params) {
    const response = await axios.post('/api/auth/microsoft/callback', params);
    auth.user = response.data.data;
}

// Fetches the CSRF cookie that Laravel then expects back as the X-XSRF-TOKEN header (axios does that).
let csrfCookie = null;
function ensureCsrfCookie() {
    if (!csrfCookie) {
        csrfCookie = axios.get('/sanctum/csrf-cookie').catch((error) => {
            csrfCookie = null;
            throw error;
        });
    }
    return csrfCookie;
}

// An expired session (any API call answering 401) ends at the login page.
let appRouter = null;
export function sessionExpired() {
    auth.user = null;
    if (appRouter && appRouter.currentRoute.value.name !== 'login') {
        appRouter.push({ name: 'login', query: { redirect: appRouter.currentRoute.value.fullPath } });
    }
}

export function setupHttp(router) {
    appRouter = router;

    axios.defaults.baseURL = API_URL;
    axios.defaults.withCredentials = true;
    axios.defaults.withXSRFToken = true;
    axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';
    axios.defaults.timeout = 1000 * 60 * 10;

    // the CSRF cookie has to exist before the first request that changes something
    axios.interceptors.request.use(async (config) => {
        if (!['get', 'head', 'options'].includes((config.method || 'get').toLowerCase()) && !config.url.endsWith('/sanctum/csrf-cookie')) {
            await ensureCsrfCookie();
        }
        return config;
    });

    axios.interceptors.response.use((response) => response, async (error) => {
        const config = error.config;
        const status = error.response && error.response.status;

        // 419: the CSRF token (cookie) is gone, e.g. the session expired and began again; fetch a new one and try once more
        if (status === 419 && config && !config._csrfRetried && !config.url.endsWith('/sanctum/csrf-cookie')) {
            config._csrfRetried = true;
            csrfCookie = null;
            return axios.request(config);
        }

        // a 401 of these calls is an answer to the page that asked (is anybody signed in? did the login work?),
        // not an expired session
        const asksAboutSession = config && (config.url.endsWith('/api/user/me') || config.url.includes('/api/auth/'));
        if (status === 401 && !asksAboutSession) {
            sessionExpired();
        }
        return Promise.reject(error);
    });
}
